import React, { useEffect, useState } from 'react';
import { CheckCircle2, KeyRound, Loader2, ShieldAlert } from 'lucide-react';

import { applyPasswordReset, checkPasswordResetCode } from '../../firebase';
import { Button } from '../ui/button';
import { BrandHeader } from './BrandHeader';

export function urlLooksLikePasswordReset(href: string = window.location.href): boolean {
  try {
    const url = new URL(href);
    return url.searchParams.get('mode') === 'resetPassword' && !!url.searchParams.get('oobCode');
  } catch {
    return false;
  }
}

function resetCode(href: string): string {
  try {
    return new URL(href).searchParams.get('oobCode') ?? '';
  } catch {
    return '';
  }
}

function clearResetParams(): void {
  const url = new URL(window.location.href);
  for (const key of ['mode', 'oobCode', 'apiKey', 'lang', 'tenantId']) {
    url.searchParams.delete(key);
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

type Phase = 'checking' | 'ready' | 'saving' | 'complete' | 'invalid';

export function PasswordResetScreen(): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  // Freeze the oobCode at mount. A successful reset calls clearResetParams(),
  // which strips `oobCode` from the URL — if we re-read it from the URL on each
  // render the validation effect below would re-fire with an empty code, reject,
  // and flip the success screen to the "link expired" screen. Capturing it once
  // keeps the effect's dependency stable across that URL change.
  const [code] = useState(() => resetCode(window.location.href));

  useEffect(() => {
    let active = true;
    void checkPasswordResetCode(code)
      .then(() => { if (active) setPhase('ready'); })
      .catch(() => { if (active) setPhase('invalid'); });
    return () => { active = false; };
  }, [code]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Use at least 8 characters');
      return;
    }
    if (password !== confirmation) {
      setError('Passwords do not match');
      return;
    }

    setPhase('saving');
    try {
      await applyPasswordReset(code, password);
      clearResetParams();
      setPhase('complete');
    } catch {
      setPhase('invalid');
    }
  }

  function returnToSignIn(): void {
    window.location.assign('/');
  }

  return (
    <main className="min-h-screen w-full bg-background font-mono flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-9">
        <BrandHeader />
        <section className="border-2 border-border bg-background p-8 space-y-6" aria-label="Reset password">
          <div className="flex items-center justify-center gap-2 text-text-secondary">
            <KeyRound className="w-4 h-4" aria-hidden="true" />
            <h1 className="text-[10px] font-bold uppercase tracking-widest">Reset password</h1>
          </div>

          {phase === 'checking' ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Checking reset link…
            </div>
          ) : phase === 'invalid' ? (
            <div className="space-y-5 text-center">
              <ShieldAlert className="w-8 h-8 mx-auto text-destructive" aria-hidden="true" />
              <p className="text-sm text-text-secondary">
                This reset link is invalid, expired, or has already been used.
              </p>
              <Button className="w-full" onClick={returnToSignIn}>Request a new link</Button>
            </div>
          ) : phase === 'complete' ? (
            <div className="space-y-5 text-center">
              <CheckCircle2 className="w-8 h-8 mx-auto text-primary" aria-hidden="true" />
              <p className="text-sm text-text-secondary">Your password has been updated.</p>
              <Button className="w-full" onClick={returnToSignIn}>Return to sign in</Button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={submit}>
              <div className="space-y-2">
                <label htmlFor="new-password" className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full h-11 px-3 text-sm border-2 border-border bg-background text-text-primary rounded-md"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="confirm-password" className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className="w-full h-11 px-3 text-sm border-2 border-border bg-background text-text-primary rounded-md"
                  required
                />
              </div>
              {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={phase === 'saving'}>
                {phase === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Saving" /> : 'Set new password'}
              </Button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
